import { memo, type FC } from 'react'
import Markdown from 'react-markdown'

import { EditableChecklistField } from './EditableChecklistField'
import { FieldHelpPopover, FieldShell, type FieldRendererProps, type FormField } from './FieldShell'
import { CHECKLIST_TYPE } from '../lib/checklistHelper'
import type { DynamicField, GenericDynamicField } from '../types'
import { DateSinglePicker } from '@components/ui/DateSinglePicker'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Input,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  CheckboxBlock,
  Label,
  SingleSelect,
  Separator,
} from '@fp/ui-kit'
import { dayjs, getAppTimezone } from '@lib/dayjs'
import { splitAndCapitalizeEachWord } from '@lib/textFormatters'
import { pickerDateToBackendIso, backendIsoToPickerDate } from '@lib/tz'
import { Calendar } from '@ui/Calendar'

export type MarkdownFieldProps = { paramsField: { value?: string } }

export const MarkdownField: FC<MarkdownFieldProps> = memo(({ paramsField }) => (
  <div className='markdown'>
    <Markdown>{`${paramsField.value || '-'}`}</Markdown>
  </div>
))

/** Value is optional: without it a bare divider, with it the text sits in the middle of the divider. */
export const DividerField: FC<{ paramsField: { value?: string } }> = memo(({ paramsField }) => {
  const value = typeof paramsField.value === 'string' ? paramsField.value : ''
  const Divider = <Separator className='flex flex-1 m-0 min-w-8' />

  return (
    <div className='w-full relative flex items-center py-1'>
      {Divider}
      {value && <div className='text-center px-2 mx-2 w-fit'>{value}</div>}
      {Divider}
    </div>
  )
})

export type FieldTypes = DynamicField['type']

export { FieldShell, type FieldRendererProps, type FormField }

export const FieldRenderer: FC<FieldRendererProps> = memo(({ paramsField, field, error, helperText }) => {
  const fieldId = paramsField.param_key ? `dynamic-field-${paramsField.param_key}` : undefined
  const inputValue = field.value ?? ''
  const shellProps = { paramsField, error, helperText }

  switch (paramsField.type) {
    case 'markdown':
      return <MarkdownField paramsField={paramsField} />

    case 'divider':
      return <DividerField paramsField={paramsField} />

    case 'selector':
      return (
        <FieldShell {...shellProps}>
          {paramsField.searchable ? (
            <SearchableSelect paramsField={paramsField} field={field} />
          ) : (
            <PlainSelect paramsField={paramsField} field={field} />
          )}
        </FieldShell>
      )

    case CHECKLIST_TYPE:
      return <EditableChecklistField paramsField={paramsField} field={field} error={error} helperText={helperText} />

    case 'checkbox': {
      const checkboxId = fieldId ? `${fieldId}-checkbox` : undefined

      return (
        <div className='flex items-center gap-2 pb-4'>
          <CheckboxBlock id={checkboxId} checked={!!field.value} onCheckedChange={(c) => field.onChange(!!c)} />
          <Label htmlFor={checkboxId}>{paramsField.placeholder || paramsField.name}</Label>
          <FieldHelpPopover fieldName={paramsField.name} helperText={helperText} />
        </div>
      )
    }

    case 'number':
      return (
        <FieldShell {...shellProps}>
          <Input
            type='number'
            placeholder={paramsField.placeholder}
            disabled={paramsField.disabled}
            {...field}
            value={inputValue}
          />
        </FieldShell>
      )

    case 'file':
      return <FileField field={paramsField} />

    case 'date':
      return (
        <FieldShell {...shellProps}>
          <Popover>
            <PopoverTrigger asChild>
              <Input
                readOnly
                value={
                  field.value
                    ? dayjs
                        .utc(field.value as string)
                        .tz(getAppTimezone())
                        .format('YYYY-MM-DD')
                    : (paramsField.placeholder ?? 'Pick a date')
                }
                className='cursor-pointer'
              />
            </PopoverTrigger>
            <PopoverContent className='p-0'>
              <Calendar
                mode='single'
                selected={field.value ? backendIsoToPickerDate(field.value as string) : undefined}
                onSelect={(d) => field.onChange(d ? pickerDateToBackendIso(d) : '')}
              />
            </PopoverContent>
          </Popover>
        </FieldShell>
      )

    case 'datetime':
      return (
        <FieldShell {...shellProps}>
          <DateSinglePicker
            value={(field.value as string | null | undefined) ?? undefined}
            disableFutureDates={false}
            onChange={(iso) => field.onChange(iso ?? undefined)}
            isFullWidth={true}
            showTime={true}
          />
        </FieldShell>
      )

    default:
      return (
        <FieldShell {...shellProps}>
          <Input
            type='text'
            placeholder={paramsField.placeholder}
            disabled={paramsField.disabled}
            {...field}
            value={inputValue}
          />
        </FieldShell>
      )
  }
})

type PlainSelectProps = {
  paramsField: GenericDynamicField
  field: FormField
}
const PlainSelect: FC<PlainSelectProps> = memo(({ paramsField, field }) => (
  <Select value={String(field.value)} onValueChange={field.onChange} disabled={paramsField.disabled}>
    <SelectTrigger>
      <SelectValue placeholder={paramsField.placeholder ?? 'Select…'} />
    </SelectTrigger>
    <SelectContent>
      {paramsField.options?.length ? (
        paramsField.options.map((o) => (
          <SelectItem key={o.value} value={String(o.value)}>
            {o.label}
          </SelectItem>
        ))
      ) : (
        <div className='py-6 text-center text-sm text-muted-foreground'>No options available.</div>
      )}
    </SelectContent>
  </Select>
))

type SearchableSelectProps = {
  paramsField: GenericDynamicField
  field: FormField
}
const SearchableSelect: FC<SearchableSelectProps> = memo(({ paramsField, field }) => (
  <SingleSelect
    {...field}
    options={typeof paramsField.options !== 'undefined' ? paramsField.options : []}
    value={field.value || ''}
    placeholder={`Select ${paramsField.name}`}
    disabled={paramsField.disabled}
    modal
  />
))

const FileField = ({ field }: { field: Partial<GenericDynamicField> }) => {
  return (
    <div className='w-full border border-muted border-b-3 border-b-primary p-2  hover:text-primary rounded'>
      <a href={field.default as string} target='_blank' rel='noopener noreferrer' className='w-full block'>
        {splitAndCapitalizeEachWord(field.name || '-')}
      </a>
    </div>
  )
}
